import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { Transition } from '@headlessui/react';
import { X } from 'lucide-react';
import Cookies from 'js-cookie';
import store, { removeFromCart } from '../../store/store';
import { calculateTotal } from '../CartHelper/CartHelper';
import FavouriteButton from '../FavouriteButton/FavouriteButton';
import Footer from '../Footer/Footer';
import './CartContainer.css';

const formatCurrency = (value) => `${Number(value || 0).toLocaleString('ru-RU')} ₽`;

const formatReserveTime = (seconds) => {
    const safeSeconds = Math.max(Number(seconds) || 0, 0);
    const minutes = Math.floor(safeSeconds / 60);
    const remainderSeconds = safeSeconds % 60;

    if (minutes === 0) {
        return `${remainderSeconds} сек.`;
    }

    return `${minutes} мин ${remainderSeconds.toString().padStart(2, '0')} сек.`;
};

const getSelectedSection = () => {
    const selectedSection = Cookies.get('selectedSection');
    return window.categories?.[selectedSection] || 'women';
};

const getImageUrl = (cartItem) => {
    const url = cartItem.Product?.ImagesPath?.$values?.[0]?.URL || '';
    return url.startsWith('/img/products/') ? url.replace('/img/products/', '/img/products/300x300/') : url;
};

const getProductUrl = (cartItem, selectedSect) => {
    if (cartItem.Product?.Url) {
        return cartItem.Product.Url;
    }

    if (cartItem.Product?.IsForOrder) {
        return `/shopping/preorder/${selectedSect}/${cartItem.Product.Sename}`;
    }

    return `/shopping/product/${selectedSect}/${cartItem.Product?.Sename}`;
};

const CartContainer = ({ data }) => {
    const cartItems = data?.Items?.$values || [];
    const selectedSect = getSelectedSection();
    const [alertVisible, setAlertVisible] = useState(false);
    const [alertMessage, setAlertMessage] = useState('');
    const [alertType, setAlertType] = useState('error');
    const [items, setItems] = useState(cartItems);
    const [timers, setTimers] = useState(cartItems.map((item) => Number.parseInt(item.ReservedForCheckoutSeconds, 10) || 0));

    useEffect(() => {
        const interval = setInterval(() => {
            setTimers((prevTimers) => prevTimers.map((timer) => (timer > 0 ? timer - 1 : 0)));
        }, 1000);

        return () => clearInterval(interval);
    }, []);

    const { total, deliveryCost, totalWithDelivery } = useMemo(() => calculateTotal(items), [items]);
    const hasCheckoutItems = items.some((item) => !item.Product?.IsForOrder && item.IsInStock && item.Product?.IsAvailable !== false);

    const showAlert = (message, type = 'error') => {
        setAlertMessage(message);
        setAlertType(type);
        setAlertVisible(true);

        setTimeout(() => {
            setAlertVisible(false);
        }, 3000);
    };

    const handleRemoveFromCart = async (productId) => {
        try {
            const response = await fetch('/Cart/Remove', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    pid: productId,
                }),
            });

            if (!response.ok) {
                const responseData = await response.json();
                showAlert(responseData.message || 'Не удалось удалить товар из корзины');
                return;
            }

            const removedItem = items.find((item) => item.Product.ProductId === productId);
            const productKey = `${removedItem?.Product?.IsForOrder ? 'aproduct' : 'product'}:${productId}`;
            store.dispatch(removeFromCart(productKey));
            setItems((currentItems) => currentItems.filter((item) => item.Product.ProductId !== productId));
            setTimers((currentTimers) => currentTimers.filter((_, index) => items[index]?.Product?.ProductId !== productId));
        } catch (error) {
            showAlert('Ошибка удаления из корзины');
        }
    };

    return (
        <>
            <section className="cart-page">
                <div className="cart-shell">
                    <div className="cart-hero">
                        <div>
                            <div className="cart-hero__eyebrow">Корзина</div>
                            <h1 className="cart-hero__title">Корзина</h1>
                            <p className="cart-hero__description">
                                Проверьте выбранные позиции перед переходом к оформлению.
                            </p>
                        </div>
                        <div className="cart-hero__meta">
                            <span>{items.length} поз.</span>
                            <span>Безопасная оплата</span>
                        </div>
                    </div>

                    {alertVisible && (
                        <Transition
                            as={Fragment}
                            show={alertVisible}
                            enter="transition-opacity duration-500"
                            enterFrom="opacity-0"
                            enterTo="opacity-100"
                            leave="transition-opacity duration-500"
                            leaveFrom="opacity-100"
                            leaveTo="opacity-0"
                        >
                            <div className={`cart-alert cart-alert--${alertType}`}>{alertMessage}</div>
                        </Transition>
                    )}

                    <div className="cart-layout">
                        <div className="cart-items">
                            {items.length === 0 ? (
                                <div className="cart-empty">
                                    <div className="cart-empty__eyebrow">Корзина пуста</div>
                                    <p className="cart-empty__text">
                                        Добавьте в корзину вещи, которые хотите сохранить для покупки или заказа.
                                    </p>
                                    <a href="/shopping/women/items" className="cart-primary-action">
                                        Перейти к каталогу
                                    </a>
                                </div>
                            ) : (
                                <ul className="cart-list">
                                    {items.map((cartItem, index) => {
                                        const isPreorder = cartItem.Product?.IsForOrder === true;
                                        const isOutOfStock = !cartItem.IsInStock || cartItem.Product?.IsAvailable === false;
                                        const imageUrl = getImageUrl(cartItem);
                                        const productUrl = getProductUrl(cartItem, selectedSect);
                                        const sizeLabel = cartItem.SizeStr || cartItem.Product?.SizeStr || (isPreorder ? 'Под заказ' : 'Размер уточняется');

                                        return (
                                            <li key={cartItem.Product.ProductId} className="cart-item">
                                                <a href={productUrl} className="cart-item__media">
                                                    <img
                                                        src={imageUrl}
                                                        alt={cartItem.Product.Title || 'Изображение товара'}
                                                        className={`cart-item__image ${isOutOfStock ? 'is-muted' : ''}`}
                                                    />
                                                    {isOutOfStock && <span className="cart-item__badge">Распродано</span>}
                                                </a>

                                                <div className="cart-item__content">
                                                    <div className="cart-item__topline">
                                                        <div>
                                                            <div className="cart-item__brand">{cartItem.Product.BrandName}</div>
                                                            <h2 className="cart-item__title">{cartItem.Product.Title}</h2>
                                                        </div>

                                                        <button
                                                            className="cart-item__remove"
                                                            onClick={() => handleRemoveFromCart(cartItem.Product.ProductId)}
                                                            aria-label="Удалить товар из корзины"
                                                        >
                                                            <X size={18} />
                                                        </button>
                                                    </div>

                                                    <div className="cart-item__meta">
                                                        <span>Размер: {sizeLabel}</span>
                                                        <span>{formatCurrency(cartItem.Price)}</span>
                                                    </div>

                                                    {timers[index] > 0 && (
                                                        <p className="cart-item__reservation">
                                                            Зарезервировано {!cartItem.ReservedByMe ? 'другим пользователем ' : ''}
                                                            на {formatReserveTime(timers[index])}
                                                        </p>
                                                    )}

                                                    <div className="cart-item__actions">
                                                        {!isPreorder && <FavouriteButton productId={cartItem.Product.ProductId} />}
                                                    </div>
                                                </div>
                                            </li>
                                        );
                                    })}
                                </ul>
                            )}
                        </div>

                        <aside className="cart-summary">
                            <div className="cart-summary__card">
                                <div className="cart-summary__eyebrow">Ваш заказ</div>
                                <h2 className="cart-summary__title">Сумма заказа</h2>

                                <div className="cart-summary__rows">
                                    <div className="cart-summary__row">
                                        <span>Товары</span>
                                        <span>{formatCurrency(total)}</span>
                                    </div>
                                    <div className="cart-summary__row">
                                        <span>Доставка</span>
                                        <span>{formatCurrency(deliveryCost)}</span>
                                    </div>
                                </div>

                                <div className="cart-summary__total">
                                    <span>Итого</span>
                                    <span>{formatCurrency(totalWithDelivery)}</span>
                                </div>

                                {!data?.IsAllowedToCheckout ? (
                                    <>
                                        <p className="cart-summary__note">
                                            Перед переходом к следующему шагу подтвердите email в личном кабинете.
                                        </p>
                                        <button
                                            onClick={() => {
                                                window.location.href = '/account';
                                            }}
                                            className="cart-primary-action"
                                        >
                                            Подтвердить email
                                        </button>
                                    </>
                                ) : (
                                    <button
                                        onClick={() => {
                                            if (hasCheckoutItems) {
                                                window.location.href = '/Checkout';
                                            }
                                        }}
                                        className="cart-primary-action"
                                        disabled={!hasCheckoutItems}
                                    >
                                        Перейти к оплате
                                    </button>
                                )}
                            </div>
                        </aside>
                    </div>
                </div>
            </section>

            <Footer />
        </>
    );
};

export default CartContainer;
